import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  MessageSquareText, Plus, Edit, Trash2, Send, Loader2, Eye, Copy, AlertCircle,
  FileText, Tag, Clock, CheckCircle, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { id: 'marketing', label: 'Marketing', color: 'bg-purple-100 text-purple-700' },
  { id: 'utility', label: 'Utilidad', color: 'bg-blue-100 text-blue-700' },
  { id: 'authentication', label: 'Autenticación', color: 'bg-green-100 text-green-700' }
];

const STATUS_CONFIG = {
  draft: { label: 'Borrador', color: 'bg-slate-100 text-slate-600', icon: FileText },
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  approved: { label: 'Aprobada', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const Templates = () => {
  const { token, isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewValues, setPreviewValues] = useState({});

  const [formData, setFormData] = useState({
    name: '',
    category: 'utility',
    content: '',
    variables: [],
    footer: '',
    buttons: []
  });

  useEffect(() => {
    fetchTemplates();
  }, [token]);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/hsm-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractVariables = (content) => {
    const regex = /\{\{(\d+)\}\}/g;
    const matches = [...content.matchAll(regex)];
    const uniquePositions = [...new Set(matches.map(m => parseInt(m[1])))];
    return uniquePositions.sort((a, b) => a - b).map(pos => ({
      position: pos,
      example: ''
    }));
  };

  const handleContentChange = (content) => {
    const variables = extractVariables(content);
    setFormData(prev => ({
      ...prev,
      content,
      variables
    }));
  };

  const handleCreateTemplate = async () => {
    if (!formData.name.trim() || !formData.content.trim()) {
      toast.error('Nombre y contenido son requeridos');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/hsm-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success('Plantilla creada exitosamente');
        setShowCreateDialog(false);
        setFormData({
          name: '',
          category: 'utility',
          content: '',
          variables: [],
          footer: '',
          buttons: []
        });
        fetchTemplates();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al crear plantilla');
      }
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error al crear plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;

    try {
      const response = await fetch(`${API_URL}/api/hsm-templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Plantilla eliminada');
        fetchTemplates();
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error al eliminar plantilla');
    }
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    const initialValues = {};
    template.variables?.forEach(v => {
      initialValues[v.position] = v.example || '';
    });
    setPreviewValues(initialValues);
    setShowPreviewDialog(true);
  };

  const getPreviewContent = () => {
    if (!selectedTemplate) return '';
    let content = selectedTemplate.content;
    Object.entries(previewValues).forEach(([pos, value]) => {
      content = content.replace(new RegExp(`\\{\\{${pos}\\}\\}`, 'g'), value || `{{${pos}}}`);
    });
    return content;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-full">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <MessageSquareText className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Acceso Restringido</h2>
            <p className="text-slate-500">Solo los administradores pueden gestionar plantillas HSM.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-[#25D366]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="templates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Outfit'] text-slate-900">Plantillas HSM</h1>
          <p className="text-sm text-slate-500">Gestiona plantillas de mensajes para WhatsApp Business API</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#25D366] hover:bg-[#1DA851]" data-testid="create-template-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Plantilla
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Crear Plantilla HSM</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nombre de la plantilla</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="ej: bienvenida_cliente"
                    data-testid="template-name-input"
                  />
                  <p className="text-xs text-slate-400">Solo letras minúsculas y guiones bajos</p>
                </div>
                <div className="space-y-2">
                  <Label>Categoría</Label>
                  <Select 
                    value={formData.category} 
                    onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}
                  >
                    <SelectTrigger data-testid="template-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contenido del mensaje</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => handleContentChange(e.target.value)}
                  placeholder="Hola {{1}}, gracias por contactar a TenaTour. Tu cotización para {{2}} está lista."
                  rows={5}
                  data-testid="template-content-input"
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-400">
                  Usa {"{{1}}"}, {"{{2}}"}, etc. para variables dinámicas
                </p>
              </div>

              {formData.variables.length > 0 && (
                <div className="space-y-2">
                  <Label>Variables detectadas</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.variables.map(v => (
                      <Badge key={v.position} variant="outline">
                        {`{{${v.position}}}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label>Pie de mensaje (opcional)</Label>
                <Input
                  value={formData.footer}
                  onChange={(e) => setFormData(prev => ({ ...prev, footer: e.target.value }))}
                  placeholder="TenaTour - Transporte Ejecutivo"
                  data-testid="template-footer-input"
                />
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancelar</Button>
              </DialogClose>
              <Button 
                onClick={handleCreateTemplate} 
                disabled={saving}
                className="bg-[#25D366] hover:bg-[#1DA851]"
                data-testid="save-template-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Crear Plantilla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-1">Plantillas HSM para WhatsApp Business API</p>
              <p className="text-blue-600">
                Las plantillas deben ser aprobadas por Meta antes de poder enviarlas. 
                Actualmente el sistema está preparado para la integración futura con WhatsApp Cloud API.
                Los mensajes se envían como texto normal hasta que se active la integración.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center">
            <MessageSquareText className="w-16 h-16 mx-auto mb-4 text-slate-200" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">No hay plantillas</h3>
            <p className="text-slate-500 mb-4">Crea tu primera plantilla HSM para WhatsApp</p>
            <Button 
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#25D366] hover:bg-[#1DA851]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crear Plantilla
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => {
            const categoryInfo = CATEGORIES.find(c => c.id === template.category);
            const statusInfo = STATUS_CONFIG[template.status] || STATUS_CONFIG.draft;
            const StatusIcon = statusInfo.icon;

            return (
              <Card key={template.template_id} className="border-slate-200 hover:border-slate-300 transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-base font-medium">{template.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge className={categoryInfo?.color || 'bg-slate-100'}>
                          {categoryInfo?.label || template.category}
                        </Badge>
                        <Badge className={statusInfo.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusInfo.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="bg-slate-50 p-3 rounded-lg mb-3 font-mono text-sm text-slate-700 whitespace-pre-wrap">
                    {template.content.length > 150 
                      ? template.content.substring(0, 150) + '...' 
                      : template.content}
                  </div>

                  {template.variables?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {template.variables.map(v => (
                        <Badge key={v.position} variant="outline" className="text-xs">
                          {`{{${v.position}}}`}
                        </Badge>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between pt-2 border-t">
                    <p className="text-xs text-slate-400">
                      {template.created_by_name}
                    </p>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openPreview(template)}
                        data-testid={`preview-template-${template.template_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(template.content)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                        className="text-red-500 hover:text-red-700"
                        data-testid={`delete-template-${template.template_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Vista Previa de Plantilla</DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-slate-500 mb-2">Mensaje:</p>
                <p className="text-slate-800 whitespace-pre-wrap">{getPreviewContent()}</p>
                {selectedTemplate.footer && (
                  <p className="text-xs text-slate-400 mt-3 pt-3 border-t">
                    {selectedTemplate.footer}
                  </p>
                )}
              </div>

              {selectedTemplate.variables?.length > 0 && (
                <div className="space-y-3">
                  <Label>Valores de prueba:</Label>
                  {selectedTemplate.variables.map(v => (
                    <div key={v.position} className="flex items-center gap-2">
                      <Badge variant="outline">{`{{${v.position}}}`}</Badge>
                      <Input
                        placeholder={`Valor para variable ${v.position}`}
                        value={previewValues[v.position] || ''}
                        onChange={(e) => setPreviewValues(prev => ({
                          ...prev,
                          [v.position]: e.target.value
                        }))}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-xs text-blue-600">
                  <strong>Para WhatsApp API:</strong> template_name: "{selectedTemplate.name}", 
                  language: "{selectedTemplate.language || 'es'}"
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Templates;
